import isEqual from 'lodash/isEqual';
import transform from 'lodash/transform';
import getIn from './getIn';
import setIn from './setIn';
import { Maybe } from 'graphql/jsutils/Maybe';
import { isNumber } from 'lodash';

export const getDiffChanges = (payload: any, state?: any) => {
  return transform(payload, (result: { [key: string]: boolean }, value: any, key: string) => {
    if (!isEqual(value, state?.[key])) {
      result[key] = true;
    }
  });
};

const mergeArray = (
  resultValue: Maybe<Array<any>>,
  submittedValue: Maybe<Array<any>>,
  currentValue: Maybe<Array<any>>
): Array<any> => {
  //Recursive merge - Array handler
  const resultLength = resultValue?.length ?? 0;
  const submittedLength = submittedValue?.length ?? 0;
  const currentLength = currentValue?.length ?? 0;
  const highest = Math.max(resultLength, submittedLength, currentLength);
  const uuidSupported: boolean =
    /.*Suppliers\/MatchPurchaseOrders\/Details.*|.*Order\/Details.*|.*Order\/Template.*|.*Quote\/Details.*|.*Invoice\/Details.*|.*Credit\/Details.*|.*ShortageDetails.*/.test(
      window.location.href
    );

  const mergedArray: Array<any> = [];
  if (uuidSupported && (resultValue?.[0]?.uuid || submittedValue?.[0]?.uuid)) {
    //Appears to be an entity and the form supports UUID merging - Perform a UUID-based merge.
    //Note - Specifically runs in "Result" order to support service-side merging (Such as on Sales Order item entry).
    for (let i = 0; i < resultLength; i++) {
      const resultElement = resultValue?.[i];
      const submitElement = submittedValue?.find((element) => element?.uuid === resultElement?.uuid);
      const currentElement = currentValue?.find((element) => element?.uuid === resultElement?.uuid);
      //If the record is archived, and we have no result - The services removed it - We should honor that/leave it removed.
      if (!(submitElement?.archived || submitElement?.deleted) || resultElement) {
        mergedArray.push(
          mergeTemporarySaveResult(
            {
              values: resultElement,
              valuesSubmitted: submitElement,
            },
            currentElement
          )
        );
      }
    }
    //Check results for any additional lines (Different UUIDs) and append them to avoid accidental data loss.
    // (Note - Unlikely, typically a temporary save would not add lines).
    submittedValue?.forEach((submitElement) => {
      if (
        !submitElement.archived &&
        !submitElement.deleted &&
        !mergedArray?.find((mergedElement) => mergedElement.uuid === submitElement.uuid)
      ) {
        mergedArray.push(submitElement);
      }
    });
    //Check current value for any additional lines (Different UUIDs) and append them to avoid accidental data loss.
    // (Note - Possible, would represent a user adding a line while a temporary save is in-flight, appending to end should be appropriate).
    currentValue?.forEach((currentElement) => {
      //Note - If deleted/archived, do not re-insert if the services remove it.
      if (
        !currentElement.archived &&
        !currentElement.deleted &&
        !mergedArray?.find((mergedElement) => mergedElement.uuid === currentElement.uuid)
      ) {
        mergedArray.push(currentElement);
      }
    });
  } else {
    //Not-an-entity - Perform an index-based merge.
    for (let i = 0; i < highest; i++) {
      mergedArray[i] = mergeTemporarySaveResult(
        {
          values: resultValue?.[i],
          valuesSubmitted: submittedValue?.[i],
        },
        currentValue?.[i]
      );
    }
  }
  //If any elements merged into being null/undefined, trim them out.
  return mergedArray.filter((value) => !!value);
};

const compareEqual = (
  submitted: string | number | null | undefined,
  result: string | number | null | undefined
): boolean => {
  if (typeof result === 'number') {
    if (typeof submitted === 'number') {
      //Both numeric, simple equals check is sufficient.
      return result === submitted;
    } else if (!submitted) {
      //Result has a type & submitted doesn't exist, can't be equivalent.
      return false;
    }
    //Parse submitted to be numeric to match the result then compare.
    //(Typically required where a text field sets a number as a string like "5.5", but the services parse it to numeric.
    const submittedNumber = parseFloat(submitted);
    return result === submittedNumber;
  }
  return result === submitted;
};

/**
 * Merge the results of a temporary save - Only values which differ from the submitted value are merged.
 * (This way, if the user edits a value on the form mid-temporary save, the users change is not over-written).
 */
const mergeTemporarySaveResult = <T = any>(
  payload: { values: Readonly<T>; valuesSubmitted: Readonly<T> },
  state: Readonly<T>
) => {
  let mergeState = state;

  const result = payload.values;
  const submitted = payload.valuesSubmitted;

  const fields = getDiffChanges(result, mergeState);

  //Check if we're doing a basic string comparison
  if ((typeof result === 'string' || typeof submitted === 'string') && result !== submitted) {
    return result;
  }

  Object.keys(fields).forEach((fieldId) => {
    const resultValue = getIn(result, fieldId);
    const submittedValue = getIn(submitted, fieldId);
    const currentValue = getIn(state, fieldId);

    if (Array.isArray(resultValue)) {
      mergeState = setIn(mergeState, fieldId, mergeArray(resultValue, submittedValue, currentValue));
    } else if ((resultValue as any)?.id || (submittedValue as any)?.id) {
      //Recursive merge - Entity handler
      mergeState = setIn(
        mergeState,
        `${fieldId}`,
        mergeTemporarySaveResult(
          {
            values: resultValue,
            valuesSubmitted: submittedValue,
          },
          currentValue
        )
      );
    } else if (!compareEqual(submittedValue, resultValue)) {
      //We should now have a primitive/non-entity value that has changed in the result - Merge.
      mergeState = setIn(mergeState, `${fieldId}`, resultValue);
    }
  });

  return mergeState;
};

export default mergeTemporarySaveResult;
